import { useEffect, useState } from 'react';
import { UserAvatar } from '../components/UserAvatar';
import {
  participantIsTyping,
  type DocumentRosterParticipant
} from './documentCollaborationTypes';
import {
  EDITING_PRESENCE_AVATAR_COLOR,
  getActivePresenceAvatarColor
} from './presenceAvatarColors';
import { TypingDots } from './TypingDots';
import './ActiveUsersBar.css';

export interface ActiveUsersBarProps {
  roster: DocumentRosterParticipant[];
  currentUserId: string;
  maxAvatars?: number;
  activeLabel?: string;
  editingLabel?: string;
  /** Immediate typing state for the current user (before SignalR roster refresh). */
  localTypingActive?: boolean;
}

/**
 * Active and editing user groups for the editor title bar. Users with unsaved changes appear under
 * Editing; animated dots show on avatars while they are actively typing.
 */
export function ActiveUsersBar({
  roster,
  currentUserId,
  maxAvatars = 5,
  activeLabel = 'Active:',
  editingLabel = 'Editing:',
  localTypingActive = false
}: ActiveUsersBarProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const hasTyping = roster.some((p) => participantIsTyping(p));
    if (!hasTyping) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 400);
    return () => window.clearInterval(timer);
  }, [roster]);

  if (!roster.length) return null;

  const activeParticipants = roster.filter((p) => !p.isEditing);
  const editingParticipants = roster.filter((p) => p.isEditing);

  return (
    <span
      className="active-users-bar"
      aria-label={`${roster.length} user(s) on this document`}
    >
      {activeParticipants.length > 0 ? (
        <PresenceGroup
          label={activeLabel}
          participants={activeParticipants}
          currentUserId={currentUserId}
          maxAvatars={maxAvatars}
          nowMs={nowMs}
          variant="active"
        />
      ) : null}
      {editingParticipants.length > 0 ? (
        <PresenceGroup
          label={editingLabel}
          participants={editingParticipants}
          currentUserId={currentUserId}
          maxAvatars={maxAvatars}
          nowMs={nowMs}
          variant="editing"
          showTypingDots
          localTypingActive={localTypingActive}
        />
      ) : null}
    </span>
  );
}

interface PresenceGroupProps {
  label: string;
  participants: DocumentRosterParticipant[];
  currentUserId: string;
  maxAvatars: number;
  nowMs: number;
  variant: 'active' | 'editing';
  showTypingDots?: boolean;
  localTypingActive?: boolean;
}

function PresenceGroup({
  label,
  participants,
  currentUserId,
  maxAvatars,
  nowMs,
  variant,
  showTypingDots = false,
  localTypingActive = false
}: PresenceGroupProps) {
  const ordered = [...participants].sort((a, b) => {
    const aSelf = isSameUser(a.userId, currentUserId) ? 1 : 0;
    const bSelf = isSameUser(b.userId, currentUserId) ? 1 : 0;
    if (aSelf !== bSelf) return aSelf - bSelf;
    return a.displayName.localeCompare(b.displayName);
  });

  const shown = ordered.slice(0, maxAvatars);
  const overflow = ordered.length - shown.length;

  return (
    <span className={`active-users-bar__group active-users-bar__group--${variant}`}>
      <span className="active-users-bar__label">{label}</span>
      <span className="active-users-bar__avatars">
        {shown.map((p) => {
          const self = isSameUser(p.userId, currentUserId);
          const title = self ? `${p.displayName} (you)` : p.displayName;
          const typing =
            showTypingDots &&
            (participantIsTyping(p, nowMs) || (self && localTypingActive));
          const accentColor =
            variant === 'editing'
              ? EDITING_PRESENCE_AVATAR_COLOR
              : getActivePresenceAvatarColor(p.userId);
          return (
            <span
              key={p.userId}
              className={`active-users-bar__avatar active-users-bar__avatar--${variant}${self ? ' active-users-bar__avatar--self' : ''}`}
              title={title}
            >
              <UserAvatar
                username={p.upn ?? p.displayName}
                displayName={p.displayName}
                initialsOnly
                accentColor={accentColor}
                size="sm"
              />
              {typing ? <TypingDots /> : null}
            </span>
          );
        })}
        {overflow > 0 ? (
          <span
            className="active-users-bar__overflow"
            title={ordered
              .slice(maxAvatars)
              .map((p) => p.displayName)
              .join(', ')}
          >
            +{overflow}
          </span>
        ) : null}
      </span>
    </span>
  );
}

function isSameUser(a: string, b: string): boolean {
  return Boolean(a) && Boolean(b) && a.toLowerCase() === b.toLowerCase();
}
